import { useTranslation } from 'next-i18next';
import React, { useCallback, useMemo } from 'react';
import { StyledCollections } from 'src/features/common/explore/containers/collections-top-filter-container/styles';
import {
    DesktopActions,
    MobileActions,
    StyledWrapper,
} from 'src/features/common/explore/containers/explore-top-filters-container/styles';
import { useCollectionsSortingOptions } from 'src/features/common/explore/hooks/use-collections-sorting-options';
import {
    useClearItems,
    useFetchCollections,
    useFilters,
    useSetFilters,
    useSetSorting,
    useSorting,
    useTotal,
} from 'src/features/common/explore/hooks/use-explore-collections';
import { ExploreSortingModal } from 'src/features/common/explore/modals/explore-sorting-modal';
import { Button } from 'src/ui/common/atoms/button';
import { FlexBox } from 'src/ui/common/atoms/flex-box';
import { Spacing } from 'src/ui/common/atoms/spacing';
import { SvgIcon } from 'src/ui/common/atoms/svg-icon';
import { Typography } from 'src/ui/common/atoms/typography';
import { Select } from 'src/ui/common/organisms/select';
import { pluralize } from 'src/utils/formatter.utils';
import { ExploreSearch } from '../../components/explore-search/explore-search';

const CollectionsTopFilterContainer = ({ isSearch }: { isSearch?: boolean }) => {
    const { t } = useTranslation(['components']);
    const sortingOptions = useCollectionsSortingOptions();
    const setSorting = useSetSorting();
    const sorting = useSorting();
    const total = useTotal();
    const clearItems = useClearItems();
    const fetchCollections = useFetchCollections();
    const filters = useFilters();
    const setFilters = useSetFilters();

    const selectedValue = useMemo(() => sortingOptions.find((el) => el.value === sorting), [sorting, sortingOptions]);

    const locale = useMemo(
        () => ({
            sort: t('exploreFilter.actions.sorting.label', { ns: 'components' }),
            totalCollections: t('exploreFilter.totalCollections', { ns: 'components', total }),
        }),
        [t, total]
    );

    const handleSortChange = useCallback(
        async (option) => {
            if (sorting !== option.value) {
                setSorting(option.value);
                clearItems();
                await fetchCollections();
            }
        },
        [clearItems, sorting, setSorting, fetchCollections]
    );

    const openSortingModal = useCallback(() => {
        ExploreSortingModal.open({ onApply: handleSortChange, sorting, sortingOptions });
    }, [handleSortChange, sorting, sortingOptions]);

    const onRefreshClick = useCallback(async () => {
        clearItems();
        await fetchCollections();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const totalCollections = useMemo(() => `${pluralize(total, locale.totalCollections)}`, [total, locale]);

    return (
        <StyledWrapper>
            <StyledCollections>
                {!isSearch && (
                    <FlexBox alignItems="center">
                        <Spacing mr="15px" mrSm="27px">
                            <Button variant="none" onClick={onRefreshClick}>
                                <SvgIcon variant="light" icon="refresh" color="white" />
                            </Button>
                        </Spacing>
                        <Typography variant="text" size="sm" color="white">
                            {totalCollections}
                        </Typography>
                    </FlexBox>
                )}
                <ExploreSearch filters={filters} setFilters={setFilters} />
                <MobileActions view="mobile" display="flex">
                    <Button variant="grey" onClick={openSortingModal}>
                        {locale.sort}
                    </Button>
                </MobileActions>
                <DesktopActions view={['laptop', 'desktop']} display="flex">
                    <Select fitParentWidth value={selectedValue} options={sortingOptions} onChange={handleSortChange} />
                </DesktopActions>
            </StyledCollections>
        </StyledWrapper>
    );
};

export default CollectionsTopFilterContainer;
